/**
 * URLs autenticadas: /{cdAlias}/… (alias = porta do contrato).
 * Rotas de menu (ds_route) e paths internos continuam sem o alias;
 * o prefixo é aplicado na navegação.
 */

export const LAST_ALIAS_KEY = "spm_last_cd_alias";

/** Primeiro segmento de path que NÃO é alias de contrato. */
export const APP_PATH_ROOTS = new Set([
  "inicio",
  "login",
  "usuarios",
  "grupos-usuarios",
  "perfis-acesso",
  "funcoes-acesso",
  "acessos-companhia",
  "entidades",
  "cad",
  "gsf",
  "ctb",
  "financeiro",
  "adm",
]);

export function normalizeAlias(alias) {
  const a = String(alias ?? "")
    .trim()
    .toLowerCase();
  return a || "";
}

export function isReservedAppSegment(segment) {
  return APP_PATH_ROOTS.has(
    String(segment ?? "")
      .trim()
      .toLowerCase()
  );
}

export function extractAliasFromPath(pathname) {
  const raw = String(pathname || "");
  const parts = raw.split("/").filter(Boolean);
  if (parts.length === 0) return "";
  if (!isReservedAppSegment(parts[0])) {
    return normalizeAlias(parts[0]);
  }
  return "";
}

export function readLastAlias() {
  try {
    const s = sessionStorage.getItem(LAST_ALIAS_KEY);
    if (s) return normalizeAlias(s);
    const l = localStorage.getItem(LAST_ALIAS_KEY);
    if (l) return normalizeAlias(l);
    if (typeof window !== "undefined" && window.location?.pathname) {
      const fromPath = extractAliasFromPath(window.location.pathname);
      if (fromPath) return fromPath;
    }
    return "";
  } catch {
    return "";
  }
}

export function persistLastAlias(alias) {
  const a = normalizeAlias(alias);
  if (!a) return;
  try {
    sessionStorage.setItem(LAST_ALIAS_KEY, a);
    localStorage.setItem(LAST_ALIAS_KEY, a);
  } catch {
    /* ignore */
  }
}

/**
 * Path da app sem o prefixo /{alias}.
 * /gfandfsolution/gsf/foo → /gsf/foo
 * /inicio → /inicio
 */
export function appPathname(pathname) {
  const raw = String(pathname || "");
  const parts = raw.split("/").filter(Boolean);
  if (parts.length === 0) return "/";
  if (!isReservedAppSegment(parts[0])) {
    const rest = parts.slice(1).join("/");
    return rest ? `/${rest}` : "/";
  }
  return raw.startsWith("/") ? raw : `/${raw}`;
}

/**
 * @param {string} alias
 * @param {string|number} [path] path interno (/inicio, gsf/…) ou já com alias
 */
export function withContractAlias(alias, path = "") {
  const a = normalizeAlias(alias);
  if (path == null || path === "") {
    return a ? `/${a}` : "/";
  }
  const s = String(path);
  if (/^https?:\/\//i.test(s)) return s;

  const qHash = s.match(/([?#].*)$/);
  const suffix = qHash ? qHash[1] : "";
  const pathOnly = qHash ? s.slice(0, -suffix.length) : s;
  let p = pathOnly.startsWith("/") ? pathOnly : `/${pathOnly}`;

  if (!a) return `${p}${suffix}`;

  if (p === `/${a}` || p.startsWith(`/${a}/`)) {
    return `${p}${suffix}`;
  }

  // Evitar /alias/alias/...
  const first = p.split("/").filter(Boolean)[0];
  if (first && normalizeAlias(first) === a) {
    return `${p}${suffix}`;
  }

  if (p === "/") return `/${a}${suffix}`;
  return `/${a}${p}${suffix}`;
}

export function loginPath(alias) {
  const a = normalizeAlias(alias);
  return a ? `/${a}` : "/";
}

export function homePath(alias) {
  return withContractAlias(alias, "/inicio");
}

/**
 * Se o primeiro segmento for rota de app (legado sem alias), devolve path
 * a prefixar com o alias em sessão; senão null.
 */
export function legacyAppPathToPrefixed(pathname, search = "", hash = "", alias) {
  const a = normalizeAlias(alias) || readLastAlias();
  if (!a) return null;
  const parts = String(pathname || "")
    .split("/")
    .filter(Boolean);
  if (parts.length === 0) return null;
  if (!isReservedAppSegment(parts[0])) return null;
  return `/${a}${pathname.startsWith("/") ? pathname : `/${pathname}`}${search || ""}${hash || ""}`;
}
